import { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import {
  AlertTriangle, Shield, Globe, Eye, Bell, BellOff, Volume2, VolumeX,
  ChevronDown, ChevronUp, ExternalLink, Clock, Zap, Skull, Bug,
  Server, Lock, FileWarning, Radio, RefreshCw, X, Maximize2, Minimize2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DarkWebAlertsWidget({ compact = false, maxAlerts = 10 }) {
  const { isDarkMode } = useTheme();
  const [alerts, setAlerts] = useState([]);
  const [connected, setConnected] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [expanded, setExpanded] = useState(!compact);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const wsRef = useRef(null);
  const audioRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Initialize audio for critical alerts
  useEffect(() => {
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2telexxAj9markup');
  }, []);

  // Play alert sound for critical findings
  const playAlertSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    
    try {
      wsRef.current = new WebSocket(`${wsUrl}/api/ws/${token}`);

      wsRef.current.onopen = () => {
        setConnected(true);
        console.log('Dark Web Alerts WebSocket connected');
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Handle dark_web_alert messages
          if (message.type === 'dark_web_alert') {
            const newAlert = {
              id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              ...message,
              receivedAt: new Date().toISOString()
            };
            
            setAlerts(prev => [newAlert, ...prev].slice(0, maxAlerts * 2));
            
            // Play sound for critical alerts
            if (message.severity === 'critical') {
              playAlertSound();
              toast.error(`CRITICAL: ${message.title}`, {
                description: `Source: ${message.source}`,
                duration: 10000
              });
            } else if (message.severity === 'high') {
              toast.warning(`HIGH: ${message.title}`, {
                description: `Source: ${message.source}`,
                duration: 5000
              });
            }
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      wsRef.current.onclose = (event) => {
        setConnected(false);
        console.log('Dark Web Alerts WebSocket closed');
        
        // Reconnect after 5 seconds
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnected(false);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [maxAlerts, playAlertSound]);

  // Setup WebSocket connection
  useEffect(() => {
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  // Get severity styles
  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: isDarkMode ? 'bg-red-950/50' : 'bg-red-50',
          border: 'border-red-500',
          badge: 'bg-red-500 text-white',
          icon: <Skull className="w-4 h-4 text-red-500" />,
          pulse: 'animate-pulse'
        };
      case 'high':
        return {
          bg: isDarkMode ? 'bg-orange-950/50' : 'bg-orange-50',
          border: 'border-orange-500',
          badge: 'bg-orange-500 text-white',
          icon: <AlertTriangle className="w-4 h-4 text-orange-500" />,
          pulse: ''
        };
      case 'medium':
        return {
          bg: isDarkMode ? 'bg-yellow-950/50' : 'bg-yellow-50',
          border: 'border-yellow-500',
          badge: 'bg-yellow-500 text-black',
          icon: <Shield className="w-4 h-4 text-yellow-500" />,
          pulse: ''
        };
      default:
        return {
          bg: isDarkMode ? 'bg-blue-950/50' : 'bg-blue-50',
          border: 'border-blue-500',
          badge: 'bg-blue-500 text-white',
          icon: <Eye className="w-4 h-4 text-blue-500" />,
          pulse: ''
        };
    }
  };

  // Get source icon
  const getSourceIcon = (source) => {
    if (source?.includes('Shodan')) return <Server className="w-3 h-3" />;
    if (source?.includes('VirusTotal')) return <Bug className="w-3 h-3" />;
    if (source?.includes('Censys')) return <Lock className="w-3 h-3" />;
    if (source?.includes('AbuseIPDB')) return <FileWarning className="w-3 h-3" />;
    if (source?.includes('MalwareBazaar')) return <Skull className="w-3 h-3" />;
    if (source?.includes('Ransomware')) return <Skull className="w-3 h-3" />;
    if (source?.includes('URLhaus')) return <Globe className="w-3 h-3" />;
    if (source?.includes('PhishTank')) return <AlertTriangle className="w-3 h-3" />;
    if (source?.includes('GreyNoise')) return <Radio className="w-3 h-3" />;
    return <Globe className="w-3 h-3" />;
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // If minimized, show only the header bar
  if (isMinimized) {
    return (
      <div className={`rounded-lg border ${isDarkMode ? 'bg-slate-900/90 border-slate-700' : 'bg-white border-gray-200'} shadow-lg`}>
        <div 
          className={`flex items-center justify-between p-3 cursor-pointer ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-50'}`}
          onClick={() => setIsMinimized(false)}
        >
          <div className="flex items-center gap-2">
            <Radio className={`w-4 h-4 ${connected ? 'text-green-500 animate-pulse' : 'text-gray-400'}`} />
            <span className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Dark Web Alerts
            </span>
            {alerts.filter(a => a.severity === 'critical').length > 0 && (
              <Badge className="bg-red-500 text-white text-xs">
                {alerts.filter(a => a.severity === 'critical').length} Critical
              </Badge>
            )}
          </div>
          <Maximize2 className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border ${isDarkMode ? 'bg-slate-900/90 border-slate-700' : 'bg-white border-gray-200'} shadow-lg overflow-hidden`}>
      {/* Header */}
      <div className={`flex items-center justify-between p-3 border-b ${isDarkMode ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <Radio className={`w-5 h-5 ${connected ? 'text-green-500 animate-pulse' : 'text-gray-400'}`} />
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Dark Web Alerts
          </h3>
          <Badge variant="outline" className={`text-xs ${connected ? 'border-green-500 text-green-500' : 'border-gray-400 text-gray-400'}`}>
            {connected ? 'LIVE' : 'Disconnected'}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="h-7 w-7 p-0"
            title={soundEnabled ? 'Mute alerts' : 'Enable sound'}
          >
            {soundEnabled ? (
              <Volume2 className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            ) : (
              <VolumeX className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAlerts([])}
            className="h-7 w-7 p-0"
            title="Clear alerts"
          >
            <X className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMinimized(true)}
            className="h-7 w-7 p-0"
            title="Minimize"
          >
            <Minimize2 className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className={`flex items-center justify-around p-2 border-b ${isDarkMode ? 'border-slate-700 bg-slate-800/30' : 'border-gray-100 bg-gray-25'}`}>
        <div className="flex items-center gap-1 text-xs">
          <span className="w-2 h-2 rounded-full bg-red-500"></span>
          <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
            Critical: {alerts.filter(a => a.severity === 'critical').length}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs">
          <span className="w-2 h-2 rounded-full bg-orange-500"></span>
          <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
            High: {alerts.filter(a => a.severity === 'high').length}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs">
          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
            Total: {alerts.length}
          </span>
        </div>
      </div>

      {/* Alerts List */}
      <ScrollArea className={compact ? 'h-[200px]' : 'h-[300px]'}>
        {alerts.length === 0 ? (
          <div className={`flex flex-col items-center justify-center h-full py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <Shield className="w-10 h-10 mb-2 opacity-30" />
            <p className="text-sm">No alerts yet</p>
            <p className="text-xs opacity-60">Real-time alerts will appear here</p>
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {alerts.slice(0, maxAlerts).map((alert) => {
              const styles = getSeverityStyles(alert.severity);
              const isExpanded = selectedAlert === alert.id;
              
              return (
                <div
                  key={alert.id}
                  className={`rounded-md border-l-4 ${styles.bg} ${styles.border} ${styles.pulse} cursor-pointer transition-all duration-200`}
                  onClick={() => setSelectedAlert(isExpanded ? null : alert.id)}
                >
                  <div className="p-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        {styles.icon}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`${styles.badge} text-[10px] px-1.5 py-0`}>
                              {alert.severity?.toUpperCase()}
                            </Badge>
                            <span className={`text-xs flex items-center gap-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {getSourceIcon(alert.source)}
                              {alert.source}
                            </span>
                          </div>
                          <p className={`text-sm font-medium mt-1 truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {alert.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1 text-xs">
                            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                              Target: {alert.target}
                            </span>
                            <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                              <Clock className="w-3 h-3 inline mr-1" />
                              {formatTime(alert.timestamp || alert.receivedAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      ) : (
                        <ChevronDown className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      )}
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-200'}`}>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Finding Type:</span>
                            <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                              {alert.finding_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </p>
                          </div>
                          <div>
                            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Confidence:</span>
                            <p className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                              {(alert.confidence * 100).toFixed(0)}%
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 w-full text-xs h-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = '/threat-intelligence';
                          }}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          View in Threat Intelligence
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      {/* Footer */}
      {alerts.length > maxAlerts && (
        <div className={`p-2 border-t text-center ${isDarkMode ? 'border-slate-700 bg-slate-800/30' : 'border-gray-100 bg-gray-50'}`}>
          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            +{alerts.length - maxAlerts} more alerts
          </span>
        </div>
      )}
    </div>
  );
}
